import * as React from 'react';
import styled from 'styled-components';
import { RegisterOptions, useFormContext } from 'react-hook-form';
import FieldWrapper from './FieldWrapper';
import { createRules } from './utils';
import { fieldBackgroundWhite, fieldBorder, grey65 } from '../../cssConstants';

// Define interface for the styled component props
interface ElementProps {
	hideLabel?: boolean;
}

const Element = styled.input<ElementProps>`
	height: 36px;
	width: 100%;
	padding: 4px 8px;
	color: ${grey65};
	background-color: ${fieldBackgroundWhite};
	border: ${fieldBorder};
	border-radius: 8px;
	line-height: 1.4285714286;
	margin-top: ${(props) => (props.hideLabel ? '15px' : '0')};
`;

const Input = (props: InputProps) => {
	// pattern must not be apart of `rest` so that it doesn't get added as an attribute to the element, so
	// that the patternMessage can be rendered instead.
	const {
		fieldName,
		label,
		icon,
		pattern,
		patternMessage,
		required = false,
		maxLength = null,
		hideLabel = false,
		...rest
	} = props;
	const { register, unregister, getValues, setValue } = useFormContext();
	const rules = {
		...createRules(required, pattern, patternMessage, maxLength),
		...(props.createRules ? props.createRules(getValues()) : {})
	} as RegisterOptions;
	const field = register(fieldName, rules);

	React.useEffect(() => {
		return () => {
			// If a field is rendered with required = true and then is not rendered, it needs to be deregistered
			// otherwise rhf will generate an error for the field and not allow the form to be submitted.
			unregister(fieldName);
		};
	}, []);

	if (rest.type === 'text') {
		const oldOnBlur = field.onBlur;
		field.onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
			setValue(fieldName, e.target.value.replace(/\s+/g, ' ').trim());
			return oldOnBlur(e);
		};
	}
	return (
		<FieldWrapper fieldName={fieldName} label={hideLabel ? null : label} icon={icon}>
			{/* Pass hideLabel explicitly to the Element component */}
			<Element
				{...field}
				{...rest}
				hideLabel={hideLabel}
				defaultValue={getValues(fieldName)}
				disabled={props.disabled}
			/>
		</FieldWrapper>
	);
};

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
	fieldName: string;
	label?: string | JSX.Element;
	icon?: JSX.Element;
	patternMessage?: string;
	disabled?: boolean;
	hideLabel?: boolean;
	createRules?: (values: unknown) => Partial<RegisterOptions>;
}

Input.defaultProps = {
	icon: null,
	patternMessage: null,
	createRules: null,
	hideLabel: false,
	disabled: false
};

export default Input;
